import { DiagnosisTypeCriteriaInput, DoctorReviewSort, GetDoctorReviewQuery } from '@/types/bff/gql-schema';
import { ReviewSortType } from '@/types/hospital';

import { Review, ReviewComment, ReviewDiagnosisDoctor, ReviewTag } from '@/models/review/review';
import { formatDateTimeWithDot } from '@/utils/date';
import useGetDoctorReviewInfinityQuery from './useGetDoctorReviewInfinityQuery';

const parseGetDoctorReview = (data: GetDoctorReviewQuery[]): Review[] => {
  return data.flatMap((page) =>
    page.doctorReview.map((review) => {
      return new Review({
        id: Number(review.reviewId),
        title: review.title,
        content: review.content,
        tags: review.tags.map(
          (tag) =>
            new ReviewTag({
              reviewId: Number(review.reviewId),
              tagId: Number(tag.tag.tagId),
              text: String(tag.tag.text),
            })
        ),
        rating: review.rating,
        writer: review.maskedPatientName,
        diagnosisCategoryName: review.diagnosisCategoryName,
        createdAt: formatDateTimeWithDot(new Date(review.createdAt)),
        isLiked: review.isLiked,
        isPrivate: review.isPrivate,
        likeCount: review.likeCount,
        isBlockedByUser: review.isBlocked,
        isCommentBlockedByUser: review.isBlocked,
        userId: Number(review.userId),
        price: null,
        doctor: new ReviewDiagnosisDoctor({
          id: Number(review.doctor.doctorId),
          medicineType: review.doctor.medicineType,
          name: review.doctor.name,
          profile: review.doctor.profileImage,
        }),
        comment: review.comments[0]
          ? new ReviewComment({
              createdAt: formatDateTimeWithDot(review.comments[0].createdAt),
              content: review.comments[0].content,
            })
          : null,
      });
    })
  );
};

export interface GetDoctorReviewsParams {
  doctorId: string;
  sort: ReviewSortType;
  diagnosisTypeCriteria?: DiagnosisTypeCriteriaInput;
}

const useGetDoctorReviews = ({ doctorId, sort, diagnosisTypeCriteria }: GetDoctorReviewsParams) => {
  const {
    data: doctorReviewData,
    isLoading: isDoctorReviewLoading,
    isFetching: isDoctorReviewFetching,
    hasNextPage: hasDoctorReviewNextPage,
    fetchNextPage: fetchNextDoctorReviewNextPage,
  } = useGetDoctorReviewInfinityQuery(
    {
      doctorReviewRequest: {
        doctorPublicId: doctorId,
        diagnosisTypeCriteria,
      },
      doctorReviewSort: getDoctorReviewSort(sort),
      pagingRequest: {
        page: 1,
        limit: 10,
      },
    },
    {
      keepPreviousData: true,
      enabled: !!doctorId,
      select: (data) => {
        return {
          pages: parseGetDoctorReview(data.pages),
          pageParams: data.pageParams,
        };
      },
    }
  );

  return {
    doctorReviewData,
    isDoctorReviewLoading,
    isDoctorReviewFetching,
    hasDoctorReviewNextPage,
    fetchNextDoctorReviewNextPage,
  };
};

export default useGetDoctorReviews;

export const getDoctorReviewSort = (sort: ReviewSortType) => {
  switch (sort) {
    case ReviewSortType.RECENT:
      return DoctorReviewSort.Newest;
    case ReviewSortType.HIGH:
      return DoctorReviewSort.HighRating;
    case ReviewSortType.LOW:
      return DoctorReviewSort.LowRating;
  }
};
